import { InputValidator, ValidationResult } from '../../validators';
import * as yup from 'yup';

const validationFunctions: {
	[key in keyof Omit<InputValidator, 'pattern_format'>]: (
		validator: InputValidator,
		value: string | undefined,
		label?: string | undefined
	) => yup.StringSchema;
} = {
	min_length: (validator, _, label) =>
		yup
			.string()
			.min(
				validator.min_length as number,
				validator.max_length !== undefined && validator.max_length === validator.min_length
					? `${label ? `${label} must` : 'Must'} contain ${validator.min_length} ${
							validator.numeric_only === true ? 'digits' : 'characters'
					  }`
					: `${label ? `${label} must` : 'Must'} contain at least ${validator.min_length} characters`
			),
	max_length: (validator, _, label) =>
		yup
			.string()
			.max(
				validator.max_length as number,
				validator.min_length !== undefined && validator.max_length === validator.min_length
					? `${label ? `${label} must` : 'Must'} contain ${validator.max_length} ${
							validator.numeric_only === true ? 'digits' : 'characters'
					  }`
					: `${label ? `${label} must` : 'Must'} contain a maximum of ${validator.max_length} characters`
			),

	min_value: (validator, _, label) =>
		yup
			.string()
			.test(
				'min_value',
				`${label ? `${label} value` : 'Value'} less than minimum of: ${validator.min_value}`,
				(value) => {
					if (validator.min_value && Number(value) < Number(validator.min_value)) {
						throw new yup.ValidationError(
							`${label ? `${label} value` : 'Value'} less than minimum of: ${validator.min_value}`,
							value,
							'min_value'
						);
					}
					return true;
				}
			),
	max_value: (validator, _, label) =>
		yup
			.string()
			.test(
				'max_value',
				`${label ? `${label} value` : 'Value'} exceeds maximum of: ${validator.max_value}`,
				(value) => {
					if (validator.max_value && Number(value) > Number(validator.max_value)) {
						throw new yup.ValidationError(
							`${label ? `${label} value` : 'Value'} exceeds maximum of: ${validator.max_value}`,
							value,
							'max_value'
						);
					}
					return true;
				}
			),
	numeric_only: (validator, _, label) => {
		if (validator.numeric_only === true) {
			if (validator.char_whitelist && validator.char_whitelist.length > 0) {
				return yup
					.string()
					.matches(
						new RegExp(`^[${validator.char_whitelist.join('\\')}$\\d]+$`),
						`Only the following characters are allowed: [${validator.char_whitelist?.join(', ')}] `
					);
			}
			return yup.string().matches(/^\d+$/, `${label ? `${label} must` : 'Must'} contain digits only`);
		}
		return yup.string();
	},
	char_whitelist: (validator) => {
		const charWhitelist = validator.char_whitelist || [];
		if (validator.numeric_only) {
			return yup
				.string()
				.matches(
					new RegExp(`^[${validator.char_whitelist?.join('\\') || ''}\\d]+$`),
					`Only numeric characters and [${validator.char_whitelist?.join(', ')}] are allowed`
				);
		}
		return yup.string().test('char_whitelist', `Allowed characters are: [${charWhitelist.join(', ')}]`, (value) => {
			if (validator.char_whitelist && validator.char_whitelist.length > 0) {
				const whitelistRegex = new RegExp(`^[${validator.char_whitelist.join('\\')}$\\d]+$`);
				if (!whitelistRegex.test(<string>value)) {
					throw new yup.ValidationError(
						`Allowed characters are: [${validator.char_whitelist.join(', ')}]`,
						value,
						'char_whitelist'
					);
				}
			}
			return true;
		});
	},
	char_blacklist: (validator, _, label) =>
		yup.string().test('char_blacklist', (value) => {
			if (typeof value !== 'undefined') {
				const blacklist = validator.char_blacklist || [];
				const blacklistMessage = `${
					label ? `${label} contains` : 'Contains'
				}  one of the following disallowed characters: [${blacklist.join(', ')}]`;
				if (blacklist.length > 0 && blacklist.some((char) => value.includes(char))) {
					throw new yup.ValidationError(blacklistMessage, value, 'char_blacklist');
				}
			}
			return true;
		}),
	pattern: (validator, _, label) =>
		yup
			.string()
			.matches(
				new RegExp(validator.pattern!),
				validator.pattern_format
					? `${label ? `${label} ` : 'Input'} does not match pattern: ${validator.pattern_format}`
					: `${label ? `${label}` : 'Input'} does not match pattern`
			)
};

const createSchema = (
	validator: InputValidator | undefined,
	value: string | undefined,
	label?: string | undefined
): yup.StringSchema | undefined => {
	if (!validator) return;

	return Object.keys(validator)
		.filter((rule) => rule !== 'pattern_format' && validator[rule as keyof InputValidator] !== undefined)
		.reduce((schema, rule) => {
			const func = validationFunctions[rule as keyof Omit<InputValidator, 'pattern_format'>];
			if (func) {
				return func(validator, value, label).concat(schema);
			}
			return schema;
		}, yup.string());
};

export const validation = (
	value: string | undefined,
	validator: InputValidator | undefined,
	required?: boolean | undefined,
	label?: string | undefined
): ValidationResult => {
	try {
		if (required && (value === undefined || value.trim() === '')) {
			throw new yup.ValidationError(`${label || 'Value'} is required`, value, 'required');
		}

		const schema = createSchema(validator, value, label);

		if (schema) {
			schema.validateSync(value);
		}

		return { isValid: true };
	} catch (error) {
		return {
			isValid: false,
			errorMessage: (error as yup.ValidationError).message || 'Validation error'
		};
	}
};
